import store from '../app/store';
import { save } from '../features/token/tokenSaver';
import {
    clearRoleSession,
    getRefreshIdentifier,
    getRoleDashboardPath,
    getRoleToken,
    getStoredRole,
    parseJwt,
    persistRoleSession,
} from './authSession';
import { getScreenHostAccount } from './getScreenHostAccount';
import { refreshJWT } from './refreshJWT';
import {
    clearUserSession,
    getUserAccessToken,
    getUserRefreshToken,
    getUserSession,
    persistUserSession,
} from './userSession';
import { normalizeUserPhone } from '../utils/userRouting';

export const TOKEN_EXPIRY_BUFFER_SECONDS = 30;

const getAccessTokenForRole = (role) =>
    getRoleToken(role) || (role === 'user' ? getUserAccessToken() : null);

export const isAccessTokenExpired = (token, bufferSeconds = TOKEN_EXPIRY_BUFFER_SECONDS) => {
    if (!token) {
        return true;
    }

    const payload = parseJwt(token);
    if (!payload?.exp) {
        return true;
    }

    return Date.now() >= (payload.exp * 1000) - (bufferSeconds * 1000);
};

export const resolveActiveRole = () => {
    const storedRole = getStoredRole();
    if (storedRole) {
        return storedRole;
    }

    if (getUserAccessToken() || localStorage.getItem('userToken')) {
        return 'user';
    }

    return null;
};

const persistRefreshedTokens = (role, token, refreshToken) => {
    store.dispatch(save(token));

    if (role === 'user') {
        persistUserSession({ token, refreshToken });
        return;
    }

    persistRoleSession({
        role,
        token,
        refreshToken,
        name: localStorage.getItem('name'),
    });
};

const refreshAccessToken = async (role) => {
    const refreshToken = role === 'user'
        ? getUserRefreshToken() || localStorage.getItem('refreshToken')
        : localStorage.getItem('refreshToken');
    const identifier = getRefreshIdentifier(refreshToken);

    if (!refreshToken || !identifier) {
        throw new Error('Missing refresh token');
    }

    const response = await refreshJWT(refreshToken, identifier);
    const token = response.data?.token;
    const newRefreshToken = response.data?.newRefreshToken;

    if (!token || !newRefreshToken) {
        throw new Error('Invalid refresh response');
    }

    persistRefreshedTokens(role, token, newRefreshToken);
    return token;
};

const clearSessionForRole = (role) => {
    if (role === 'user') {
        clearUserSession();
        return;
    }
    clearRoleSession();
};

export const getLoginPathForRole = (role) => {
    if (role === 'admin') {
        return '/adminLogin/';
    }
    if (role === 'advertiser') {
        return '/advertiserLogin/';
    }
    if (role === 'user') {
        return '/screenHostLogin/';
    }
    return '/';
};

// When the active role can't be resolved (e.g. the session was already cleared
// by a failed proactive refresh) fall back to the role implied by the current
// route so a 401 on the user dashboard still lands on the user login instead of
// the marketing home page.
const inferRoleFromLocation = () => {
    if (typeof window === 'undefined') {
        return null;
    }
    const path = window.location.pathname || '';
    if (path.startsWith('/adminDashboard') || path.startsWith('/adminLogin')) {
        return 'admin';
    }
    if (path.startsWith('/advertiser')) {
        return 'advertiser';
    }
    if (path.startsWith('/user') || path.startsWith('/screen-host') || path.startsWith('/screenHost')) {
        return 'user';
    }
    return null;
};

export const redirectToLogin = (role) => {
    const targetRole = role || inferRoleFromLocation();
    store.dispatch(save(''));
    if (targetRole) {
        clearSessionForRole(targetRole);
    } else {
        clearUserSession();
    }

    // Don't reload the page if the user is already on the login screen —
    // that would wipe whatever they are typing into the form.
    const loginPath = getLoginPathForRole(targetRole);
    const currentPath = window.location.pathname.replace(/\/+$/, '');
    if (currentPath === loginPath.replace(/\/+$/, '')) {
        return;
    }
    window.location.href = loginPath;
};

let refreshPromise = null;

export const performTokenRefresh = (role) => {
    if (!refreshPromise) {
        refreshPromise = refreshAccessToken(role).finally(() => {
            refreshPromise = null;
        });
    }
    return refreshPromise;
};

/**
 * Returns a valid access token, refreshing it first when expired.
 * Used by axios and dashboard session hooks.
 */
export const ensureValidAccessToken = async (roleOverride) => {
    const role = roleOverride || resolveActiveRole();
    if (!role) {
        return null;
    }

    const token = getAccessTokenForRole(role);
    if (!token) {
        return null;
    }

    if (!isAccessTokenExpired(token)) {
        store.dispatch(save(token));
        return token;
    }

    try {
        return await performTokenRefresh(role);
    } catch {
        clearSessionForRole(role);
        return null;
    }
};

const buildScreenHostNavigationState = async (token) => {
    const session = getUserSession() || {};
    const payload = parseJwt(token) || {};
    let userId = session.userId ?? session.UserId ?? payload.id ?? localStorage.getItem('userId');
    let phoneNumber = session.phoneNumber ?? payload.phoneNumber;

    if (!userId || !phoneNumber) {
        const accountResponse = await getScreenHostAccount(token);
        const account = accountResponse.data || {};
        userId = userId || account.userId;
        phoneNumber = phoneNumber || account.phoneNumber;
    }

    if (userId) {
        localStorage.setItem('userId', String(userId));
    }

    const normalizedPhone = phoneNumber ? normalizeUserPhone(phoneNumber) : undefined;
    const nextSession = {
        ...session,
        token,
        refreshToken: getUserRefreshToken(),
        userId: userId ? Number(userId) : undefined,
        phoneNumber: normalizedPhone,
    };

    persistUserSession(nextSession);

    return {
        userId: userId ? Number(userId) : undefined,
        phoneNumber: normalizedPhone,
        token,
    };
};

/**
 * Validates the stored access token, refreshes it when expired, and returns
 * dashboard navigation details with up-to-date session state.
 */
export const ensureSessionCredentials = async (role) => {
    if (!role) {
        return { ok: false };
    }

    const token = await ensureValidAccessToken(role);
    if (!token) {
        return { ok: false };
    }

    const dashboardPath = getRoleDashboardPath(role);

    if (role === 'user') {
        try {
            const navigationState = await buildScreenHostNavigationState(token);
            if (!navigationState.userId) {
                return { ok: false };
            }

            return {
                ok: true,
                role,
                token,
                dashboardPath,
                navigationState,
            };
        } catch {
            clearSessionForRole(role);
            return { ok: false };
        }
    }

    return {
        ok: true,
        role,
        token,
        dashboardPath,
        navigationState: { token },
    };
};
